import React, { createContext, useCallback, useContext, useState, type Dispatch, type SetStateAction } from 'react';

interface UpgradeContextType {
  isUpgradeModalOpen: boolean;
  upgradeReason: string | null;
  /** One concrete, caller-composed line shown above the plan grid — e.g. the
   * exact address the user would get ("Get pmc-books@receiptsai.com with
   * Premium+"). Renders alongside any reason banner. */
  upgradeHighlight: string | null;
  /** Stripe coupon id to pre-apply at checkout (e.g. a nurture 50% code),
   *  carried in via ``/upgrade?coupon_id=`` and forwarded to /api/stripe/checkout. */
  upgradeCouponId: string | null;
  openUpgradeModal: (reason?: string | null, opts?: { highlight?: string | null; couponId?: string | null }) => void;
  closeUpgradeModal: () => void;
  setIsUpgradeModalOpen: Dispatch<SetStateAction<boolean>>;
}

const UpgradeContext = createContext<UpgradeContextType>(undefined!);

export const useUpgrade = () => {
  const context = useContext(UpgradeContext);
  if (!context) {
    throw new Error('useUpgrade must be used within an UpgradeProvider');
  }
  return context;
};

export const UpgradeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<string | null>(null);
  const [upgradeHighlight, setUpgradeHighlight] = useState<string | null>(null);
  const [upgradeCouponId, setUpgradeCouponId] = useState<string | null>(null);

  // Stable identities: pages open the modal from effects that list these as
  // dependencies, so a fresh function per render would re-fire those effects
  // on every provider state change (and reopen the modal right after closing).
  const openUpgradeModal = useCallback(
    (reason: string | null = null, opts?: { highlight?: string | null; couponId?: string | null }) => {
      setUpgradeReason(reason);
      setUpgradeHighlight(opts?.highlight ?? null);
      setUpgradeCouponId(opts?.couponId ?? null);
      setIsUpgradeModalOpen(true);
    },
    []
  );

  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false);
    setUpgradeCouponId(null);
  }, []);

  const value: UpgradeContextType = {
    isUpgradeModalOpen,
    upgradeReason,
    upgradeHighlight,
    upgradeCouponId,
    openUpgradeModal,
    closeUpgradeModal,
    setIsUpgradeModalOpen,
  };

  return (
    <UpgradeContext.Provider value={value}>
      {children}
    </UpgradeContext.Provider>
  );
};

export default UpgradeContext;
